import React, { useState, useEffect } from 'react';
import { Form, Button, Badge, Alert, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Star, XCircle } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } }
};

function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loginActivity, setLoginActivity] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeDays, setUpgradeDays] = useState('');
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');

  useEffect(() => {
    fetchAccount();
  }, [id]);

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/accounts/${id}`);
      setAccount(response.data.user);
      setSubscriptions(response.data.subscriptions || []);
      setLoginActivity(response.data.loginActivity || []);
      setUploadedFiles(response.data.uploadedFiles || []);
      setFormData({
        first_name: response.data.user.first_name,
        last_name: response.data.user.last_name,
        phone: response.data.user.phone || '',
        is_active: response.data.user.is_active,
        role: response.data.user.role,
        gender: response.data.user.gender || ''
      });
    } catch (err) {
      setError('Failed to load account');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/admin/accounts/${id}`, formData);
      toast.success('Account updated successfully');
      setEditing(false);
      fetchAccount();
    } catch (err) {
      toast.error('Failed to update account');
      console.error(err);
    }
  };

  const handleUpgrade = async () => {
    try {
      await api.post(`/admin/accounts/${id}/upgrade-pro`, {
        duration_days: upgradeDays ? parseInt(upgradeDays) : null
      });
      toast.success('Account upgraded to pro successfully');
      setShowUpgradeModal(false);
      setUpgradeDays('');
      fetchAccount();
    } catch (err) {
      toast.error('Failed to upgrade account');
      console.error(err);
    }
  };

  const handleDowngrade = async () => {
    if (window.confirm('Are you sure you want to downgrade this account from pro?')) {
      try {
        await api.post(`/admin/accounts/${id}/downgrade`);
        toast.success('Account downgraded successfully');
        fetchAccount();
      } catch (err) {
        toast.error('Failed to downgrade account');
        console.error(err);
      }
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error || !account) {
    return (
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <Alert variant="danger">{error || 'Account not found'}</Alert>
        <Button variant="secondary" onClick={() => navigate('/accounts')}>
          <ArrowLeft size={16} />
          Back to Accounts
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Header */}
      <div className="action-bar" style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button variant="secondary" onClick={() => navigate('/accounts')}>
            <ArrowLeft size={16} />
            Back
          </Button>
          <div>
            <h1 className="page-title" style={{ fontSize: '22px', marginBottom: 0 }}>Account Details</h1>
            <p className="page-subtitle" style={{ marginTop: '2px' }}>{account.email}</p>
          </div>
        </div>
        <div className="page-actions">
          {account.subscription_plan === 'pro' ? (
            <Button variant="warning" onClick={handleDowngrade}>
              <XCircle size={16} />
              Downgrade from Pro
            </Button>
          ) : (
            <Button variant="success" onClick={() => setShowUpgradeModal(true)}>
              <Star size={16} />
              Upgrade to Pro
            </Button>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <motion.div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {/* Account Info */}
        <motion.div className="glass-card" variants={itemVariants}>
          <div className="card-header-row">
            <h3 className="card-title">Account Information</h3>
            {!editing ? (
              <Button variant="outline-primary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="success" size="sm" onClick={handleSave}>
                  <Save size={13} />
                  Save
                </Button>
                <Button variant="secondary" size="sm" onClick={() => {
                  setEditing(false);
                  fetchAccount();
                }}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={account.email} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                disabled={!editing}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                disabled={!editing}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!editing}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                disabled={!editing}
              >
                <option value="shadchan">Shadchan</option>
                <option value="single">Single</option>
                <option value="parent">Parent</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={formData.gender || ''}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                disabled={!editing}
              >
                <option value="">Not set</option>
                <option value="boys">Boy</option>
                <option value="girls">Girl</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                disabled={!editing}
              />
            </Form.Group>
          </Form>
        </motion.div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Subscription Info */}
          <motion.div className="glass-card" variants={itemVariants}>
            <div className="card-header-row">
              <h3 className="card-title">Subscription</h3>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Plan</span>
              <span className="data-pair-value">
                <Badge bg={account.subscription_plan === 'pro' ? 'success' : 'secondary'}>
                  {account.subscription_plan || 'free'}
                </Badge>
              </span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Status</span>
              <span className="data-pair-value">{account.subscription_status || 'N/A'}</span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Period End</span>
              <span className="data-pair-value">
                {account.subscription_current_period_end
                  ? new Date(account.subscription_current_period_end).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Stripe Customer</span>
              <span className="data-pair-value" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                {account.stripe_customer_id || 'N/A'}
              </span>
            </div>

            {subscriptions.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  History
                </h4>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Start</th>
                      <th>End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td>
                          <Badge bg={sub.status === 'active' ? 'success' : 'secondary'}>
                            {sub.status}
                          </Badge>
                        </td>
                        <td>{new Date(sub.current_period_start).toLocaleDateString()}</td>
                        <td>{new Date(sub.current_period_end).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* Statistics */}
          <motion.div className="glass-card" variants={itemVariants}>
            <div className="card-header-row">
              <h3 className="card-title">Statistics</h3>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Single Profiles</span>
              <span className="data-pair-value">{account.single_profiles_count || 0}</span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Shadchan Profiles</span>
              <span className="data-pair-value">{account.shadchan_profiles_count || 0}</span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Parent Profiles</span>
              <span className="data-pair-value">{account.parent_profiles_count || 0}</span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Created</span>
              <span className="data-pair-value">{new Date(account.created_at).toLocaleString()}</span>
            </div>
            <div className="data-pair">
              <span className="data-pair-label">Last Updated</span>
              <span className="data-pair-value">{new Date(account.updated_at).toLocaleString()}</span>
            </div>
          </motion.div>

          <motion.div className="glass-card" variants={itemVariants}>
            <div className="card-header-row">
              <h3 className="card-title">Login Activity</h3>
            </div>
            {loginActivity.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                No login activity tracked yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>IP</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginActivity.map((item) => (
                      <tr key={item.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(item.created_at).toLocaleString()}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{item.ip_address || '—'}</td>
                        <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.user_agent || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          <motion.div className="glass-card" variants={itemVariants}>
            <div className="card-header-row">
              <h3 className="card-title">Uploaded Files</h3>
            </div>
            {uploadedFiles.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                No uploaded files found for this account.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedFiles.map((file) => (
                      <tr key={`${file.source}-${file.id}`}>
                        <td>
                          {file.file_url ? (
                            <a
                              href={`${apiBase}${file.file_url.startsWith('/') ? file.file_url : `/${file.file_url}`}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {file.file_name}
                            </a>
                          ) : (
                            file.file_name
                          )}
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{(file.source || '').replace('_', ' ')}</td>
                        <td>{formatBytes(file.size_bytes)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(file.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Upgrade Modal — Slide-over */}
      <Modal
        show={showUpgradeModal}
        onHide={() => setShowUpgradeModal(false)}
        className="slide-over-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upgrade to Pro</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '24px' }}>
            Upgrade <strong>{account.email}</strong> to a Pro subscription. Leave duration empty for a permanent upgrade.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Duration (days)</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g., 30, 90, 365"
              value={upgradeDays}
              onChange={(e) => setUpgradeDays(e.target.value)}
            />
            <Form.Text className="text-muted">
              Leave empty to upgrade permanently, or enter number of days for temporary upgrade
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpgradeModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleUpgrade}>
            <Star size={16} />
            Upgrade to Pro
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
}

export default AccountDetail;
